import { Injectable } from '@angular/core';
import { RewardsAdminService } from '../rewards-admin/rewards-admin.service';
import { TeamCompaniesService } from '../teamcompanies/teamcompanies.service';
import { TeamsService } from '../teams/teams.service';

export interface PayrollSnackRewardItem {
  id: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  bannerSubtitleHtml?: string;
  bannerButtonTitles?: string[];
  bannerWidth?: string;
  bannerBackgroundImage?: string;
  bannerBackgroundSize?: string;
  backgroundColor?: string;
  textColor?: string;
  detailHeaderHtml?: string;
  detailContentsHtml?: string;
  detailButtonTitles?: string[];
}

export interface PayrollSnackRewardsLoadResult {
  company: any;
  team?: any;
  brand: string;
  snackRewards: PayrollSnackRewardItem[];
  companySnackRewardIdsInOrder: string[];
  baselineSelectedIds: string[];
}

export interface AssignSnackRewardsToTeamsResult {
  persistedIds: string[];
  metadataByTeamId: Record<string, any>;
}

export interface PersistTeamSnackRewardsResult {
  metadata: any;
  persistedIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PayrollSnacksAssignmentService {
  constructor(
    private teamCompaniesService: TeamCompaniesService,
    private teamsService: TeamsService,
    private rewardsAdminService: RewardsAdminService,
  ) {}

  async loadSnackRewardsForCompany(
    companyId: string,
    teamId?: string,
    fallbackBrand = '',
    companyOverride?: any,
  ): Promise<PayrollSnackRewardsLoadResult> {
    const [company, team] = await Promise.all([
      companyOverride
        ? Promise.resolve(companyOverride)
        : this.teamCompaniesService.getCompany(companyId),
      teamId ? this.teamsService.getTeamInfo(teamId) : Promise.resolve(undefined),
    ]);
    const brand = this.resolveCompanySnackRewardBrand(company, fallbackBrand);
    const rewards = brand
      ? await this.rewardsAdminService.getPayrollAdminSnackRewardCandidates(companyId, brand)
      : [];
    const snackRewards = this.normalizeRewards(rewards);
    const companySnackRewardIdsInOrder = snackRewards.map((reward) => reward.id);

    return {
      company,
      team,
      brand,
      snackRewards,
      companySnackRewardIdsInOrder,
      baselineSelectedIds: this.getSelectedSnackRewardIds(companySnackRewardIdsInOrder, team),
    };
  }

  assignSnackRewardsToTeams(
    teamIds: string[],
    selectedRewardIds: string[],
    companySnackRewardIdsInOrder: string[],
    metadataByTeamId: Record<string, any> = {},
  ): Promise<AssignSnackRewardsToTeamsResult> {
    const persistedIds = this.getPersistedSelectedSnackRewardIds(
      selectedRewardIds,
      companySnackRewardIdsInOrder,
    );
    const nextMetadataByTeamId: Record<string, any> = {};

    return Promise.all(
      this.normalizeStringArray(teamIds).map(async (teamId) => {
        const nextMetadata = this.buildNextTeamSnackMetadata(
          metadataByTeamId[teamId] || {},
          persistedIds,
        );
        nextMetadataByTeamId[teamId] = nextMetadata;
        await this.teamsService.modifyTeam(teamId, { metadata: nextMetadata });
      })
    ).then(() => ({
      persistedIds,
      metadataByTeamId: nextMetadataByTeamId,
    }));
  }

  async persistTeamSnackRewards(
    teamId: string,
    teamInfo: any,
    selectedRewardIds: string[],
    companySnackRewardIdsInOrder: string[],
  ): Promise<PersistTeamSnackRewardsResult> {
    const persistedIds = this.getPersistedSelectedSnackRewardIds(
      selectedRewardIds,
      companySnackRewardIdsInOrder,
    );
    const nextMetadata = this.buildNextTeamSnackMetadata(
      teamInfo?.metadata || {},
      persistedIds,
    );

    await this.teamsService.modifyTeam(teamId, { metadata: nextMetadata });

    return {
      metadata: nextMetadata,
      persistedIds,
    };
  }

  getSelectedSnackRewardIds(companySnackRewardIdsInOrder: string[], team?: any): string[] {
    const availableIds = this.normalizeStringArray(companySnackRewardIdsInOrder);
    if (!team?.id) {
      return [];
    }

    const metadata = team?.metadata || {};
    if (!this.hasOwnField(metadata, 'snackRewardIds')) {
      return [];
    }

    return this.getPersistedSelectedSnackRewardIds(metadata.snackRewardIds, availableIds);
  }

  getPersistedSelectedSnackRewardIds(
    selectedRewardIds: string[],
    companySnackRewardIdsInOrder: string[],
  ): string[] {
    const selectedRewardIdSet = new Set(this.normalizeStringArray(selectedRewardIds));
    return this.normalizeStringArray(companySnackRewardIdsInOrder)
      .filter((rewardId) => selectedRewardIdSet.has(rewardId));
  }

  private buildNextTeamSnackMetadata(existingMetadata: any, persistedIds: string[]): any {
    return {
      ...(existingMetadata || {}),
      snackRewardIds: persistedIds,
    };
  }

  private normalizeRewards(rewards: any[]): PayrollSnackRewardItem[] {
    return (Array.isArray(rewards) ? rewards : [])
      .filter((reward) => !!`${reward?.id || ''}`.trim() && !this.toObject(reward?.rewardDetails)?.clo)
      .map((reward) => this.toSnackRewardItem(reward))
      .sort((left, right) => left.title.localeCompare(right.title));
  }

  private toSnackRewardItem(reward: any): PayrollSnackRewardItem {
    const bannerDetails = this.toObject(reward?.bannerDetails);
    const rewardDetails = this.toObject(reward?.rewardDetails);
    const startPage = this.getStartPage(rewardDetails);
    const title = `${bannerDetails?.title || reward?.name || reward?.id || ''}`.trim();
    const bannerSubtitleHtml = `${bannerDetails?.subtitle || ''}`.trim();
    const subtitle = this.stripHtml(bannerSubtitleHtml);
    const imageUrl = `${bannerDetails?.imageUrl || bannerDetails?.logoUrl || rewardDetails?.imageUrl || reward?.imageUrl || ''}`.trim();

    return {
      id: `${reward.id || ''}`.trim(),
      title,
      subtitle,
      imageUrl,
      bannerSubtitleHtml,
      bannerButtonTitles: this.getButtonTitles(bannerDetails?.buttons),
      bannerWidth: this.normalizeCssDimension(bannerDetails?.width, '250px'),
      bannerBackgroundImage: `${bannerDetails?.backgroundImage || ''}`.trim(),
      bannerBackgroundSize: `${bannerDetails?.backgroundSize || 'auto'}`.trim(),
      backgroundColor: `${reward?.backgroundColor || '#ffffff'}`.trim(),
      textColor: `${reward?.textColor || '#000000'}`.trim(),
      detailHeaderHtml: `${rewardDetails?.header || ''}`.trim(),
      detailContentsHtml: `${startPage?.contents || ''}`.trim(),
      detailButtonTitles: this.getButtonTitles(startPage?.buttons),
    };
  }

  private getStartPage(rewardDetails: any): any {
    const pages = Array.isArray(rewardDetails?.pages) ? rewardDetails.pages : [];
    if (!pages.length) {
      return {};
    }

    const startId = `${rewardDetails?.startId || ''}`.trim();
    return pages.find((page: any) => `${page?.id || ''}`.trim() === startId) || pages[0] || {};
  }

  private getButtonTitles(buttons: any[]): string[] {
    return (Array.isArray(buttons) ? buttons : [])
      .map((button) => `${button?.title || ''}`.trim())
      .filter(Boolean);
  }

  private normalizeCssDimension(value: any, fallback: string): string {
    const normalized = `${value || ''}`.trim();
    if (/^\d+(\.\d+)?(px|rem|em|%)$/.test(normalized)) {
      return normalized;
    }
    return fallback;
  }

  private stripHtml(value: string): string {
    return `${value || ''}`
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private resolveCompanySnackRewardBrand(company: any, fallbackBrand = ''): string {
    const metadata = this.toObject(company?.metadata);
    const payrollConfig = this.toObject(company?.payrollConfig);
    const resolvedPayrollConfig = this.toObject(company?.resolvedPayrollConfig);
    const partners = [
      company?.snackBrand,
      ...(Array.isArray(resolvedPayrollConfig?.partners) ? resolvedPayrollConfig.partners : []),
      ...(Array.isArray(payrollConfig?.partners) ? payrollConfig.partners : []),
      ...(Array.isArray(metadata?.partners) ? metadata.partners : []),
      company?.brand,
      metadata?.brand,
      payrollConfig?.brand,
      resolvedPayrollConfig?.brand,
      fallbackBrand,
    ];

    return partners
      .map((partner) => this.normalizePartnerBrand(partner))
      .find((brand) => !!brand) || '';
  }

  private normalizePartnerBrand(partner: any): string {
    const values = partner && typeof partner === 'object'
      ? [
        partner.shortName,
        partner.brand,
        partner.slug,
        partner.name,
        partner.partnerName,
        partner.id,
        partner.partnerId,
      ]
      : [partner];
    const normalizedValues = values
      .map((value) => this.normalizeBrandValue(value))
      .filter((value) => !!value);

    return normalizedValues.find((value) => !this.isUuidLike(value)) || '';
  }

  private normalizeBrandValue(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    const shortName = normalized.includes('.') ? normalized.split('.')[0] : normalized;
    return shortName.replace(/[^a-z0-9_-]/g, '');
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private normalizeStringArray(values: any[]): string[] {
    return Array.from(new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => `${value || ''}`.trim())
        .filter(Boolean)
    ));
  }

  private hasOwnField(value: any, field: string): boolean {
    return Object.prototype.hasOwnProperty.call(value || {}, field);
  }

  private toObject(value: any): any {
    if (!value) {
      return {};
    }
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  }
}
